import React, { useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbModal from '../security-toolbox-ui/StbModal';
import { useSaveLabRow } from '../../hooks/lab-hooks';
import {
  LabPhase,
  LabScenario,
  LabScenarioStep,
  LAB_ACTION_TYPES,
  LAB_AUTHORITIES,
  LAB_BLAST_RADII,
  LAB_INSERT_MODES,
  LAB_PHASES,
  LAB_PRECONDITION_SCOPES,
  LAB_TABLES,
  labSlugify,
} from '../../support/labService';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import {
  LabCheckbox,
  LabDerivedKey,
  LabFieldRow,
  LabOptionSelect,
  LabTristate,
  labErrorMessage,
} from './LabShared';
import { LabSpecBuilder, LabSpecContext } from './LabSpecFields';

export type LabStepEditorModalProps = {
  open: boolean;
  scenario: LabScenario;
  step: LabScenarioStep | null;
  /**
   * Which phase a new step starts in. Set by callers that only show one part of the lab - a
   * check authored from the validation view defaulting to bootstrap is a check that fails the
   * lab before anybody has touched it.
   */
  defaultPhase?: LabPhase;
  onClose: () => void;
  onSaved?: (step: LabScenarioStep) => void;
};

const newStep = (scenario: LabScenario, phase: LabPhase = 'setup'): Partial<LabScenarioStep> => ({
  scenario: scenario.id,
  key: '',
  name: '',
  description: '',
  phase,
  order: ((scenario.steps || []).reduce((max, step) => Math.max(max, step.order || 0), 0) || 0) + 10,
  action_type: 'command',
  timeout_seconds: 120,
  retry_limit: 0,
  optional: false,
  depends_on: [],
  gated: false,
  insert_mode: 'at_start',
  skip_if_past: null,
  // Both deliberately unset. See the blast-radius section below.
  disruptive: null,
  blast_radius: null,
  idempotent: true,
  exclusive: false,
  destructive: false,
  sensitive_output: false,
  authority: 'agent_automatic',
  enabled: true,
});

const SCOPE_VALUES = LAB_PRECONDITION_SCOPES.map((scope) => scope.value) as string[];

/**
 * Author a step.
 *
 * The interesting work here is refusing to save things the backend would refuse, or worse,
 * accept and then behave surprisingly on:
 *
 * - `disruptive` and `blast_radius` have no defaults. A step with either unset can be part
 *   of a lab but can never be pushed at running pods, and this says so instead of leaving
 *   somebody to discover it during an event.
 * - A catch-up step needs an anchor, because "already past it" is meaningless otherwise.
 * - A catch-up step must not be another step's dependency: it is skipped for anyone past
 *   the anchor, and a dependency that is skipped blocks its dependents forever.
 * - `requires` keys come from a closed set of precondition scopes; the loader refuses
 *   anything else.
 */
const LabStepEditorModal: React.FC<LabStepEditorModalProps> = ({
  open,
  scenario,
  step,
  defaultPhase,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabScenarioStep>>(() =>
    newStep(scenario, defaultPhase),
  );
  const [jsonErrors, setJsonErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string | null>(null);
  /** Bumped every time the form is re-seeded, so the JSON editors re-read their values. */
  const [formSeed, setFormSeed] = useState(0);

  const save = useSaveLabRow<Partial<LabScenarioStep> & { id?: number }>(LAB_TABLES.step);

  const siblings = useMemo(
    () => (scenario.steps || []).filter((candidate) => candidate.id !== step?.id),
    [scenario.steps, step?.id],
  );

  const devices = scenario.devices || [];

  useEffect(() => {
    if (!open) return;

    setDraft(
      step
        ? { ...step, depends_on: [...(step.depends_on || [])] }
        : newStep(scenario, defaultPhase),
    );
    setJsonErrors({});
    setFormError(null);
    setFormSeed((seed) => seed + 1);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step?.id]);

  const set = (patch: Partial<LabScenarioStep>) => setDraft((current) => ({ ...current, ...patch }));

  /**
   * The key follows the name until the step exists, then stops.
   *
   * A saved step's key is referred to by criteria, triggers, injections and other steps' dependency
   * lists, none of which are updated by a rename here - so it freezes at the first save, and the form
   * says which of the two states it is in rather than leaving that to be discovered.
   */
  const setName = (name: string) => set(step ? { name } : { name, key: labSlugify(name) });

  /** What the specs below can offer rather than ask to be typed. */
  const specContext = useMemo<LabSpecContext>(
    () => ({
      deviceKeys: (scenario.devices || []).map((device) => device.device_key).filter(Boolean),
      stepKeys: (scenario.steps || []).map((candidate) => candidate.key).filter(Boolean),
    }),
    [scenario.devices, scenario.steps],
  );

  const setJson = (field: keyof LabScenarioStep) => (
    value: Record<string, any> | null,
    error: string | null,
  ) => {
    setJsonErrors((current) => ({ ...current, [field]: error }));
    if (!error) set({ [field]: value } as Partial<LabScenarioStep>);
  };

  const toggleDependency = (stepId: number) =>
    setDraft((current) => {
      const dependsOn = current.depends_on || [];

      return {
        ...current,
        depends_on: dependsOn.includes(stepId)
          ? dependsOn.filter((id) => id !== stepId)
          : [...dependsOn, stepId],
      };
    });

  /** Whoever depends on this step, if this step is about to become catch-up. */
  const dependents = useMemo(
    () =>
      step?.id
        ? siblings.filter((candidate) => (candidate.depends_on || []).includes(step.id))
        : [],
    [siblings, step?.id],
  );

  /** Catch-up steps we have been asked to depend on. Same problem from the other side. */
  const catchUpDependencies = useMemo(
    () =>
      (draft.depends_on || [])
        .map((id) => siblings.find((candidate) => candidate.id === id))
        .filter((candidate) => candidate?.insert_mode === 'catch_up'),
    [draft.depends_on, siblings],
  );

  const injectionNotes = useMemo(() => {
    const notes: string[] = [];

    if (draft.insert_mode === 'at_start') {
      notes.push('Part of the lab from the start. It cannot be pushed at running pods.');
    }

    if (draft.disruptive === null || draft.disruptive === undefined) {
      notes.push('Disruptive is not stated, so this step is not eligible for injection.');
    }

    if (!draft.blast_radius) {
      notes.push('Blast radius is not stated, so this step is not eligible for injection.');
    }

    return notes;
  }, [draft.insert_mode, draft.disruptive, draft.blast_radius]);

  const handleSubmit = async () => {
    const firstJsonError = Object.entries(jsonErrors).find(([, error]) => !!error);

    if (firstJsonError) {
      setFormError(`${firstJsonError[0]}: ${firstJsonError[1]}`);
      return;
    }

    if (!draft.name?.trim()) {
      setFormError('A step needs a name.');
      return;
    }

    if (!draft.key?.trim()) {
      setFormError(
        'No key could be derived from that name. Use letters or digits somewhere in it - injections and criteria refer to steps by key.',
      );
      return;
    }

    if (draft.insert_mode === 'catch_up' && !draft.skip_if_past) {
      setFormError(
        'A catch-up step needs an anchor step. Without one there is no way to tell who is already past it.',
      );
      return;
    }

    if (draft.insert_mode === 'catch_up' && dependents.length) {
      setFormError(
        `${dependents
          .map((dependent) => dependent.key)
          .join(', ')} depend${dependents.length === 1 ? 's' : ''} on this step. A catch-up step ` +
          'is skipped for anyone past the anchor, and a skipped dependency blocks its dependents ' +
          'for good. Remove the dependency first.',
      );
      return;
    }

    if (catchUpDependencies.length) {
      setFormError(
        `${catchUpDependencies
          .map((dependency) => dependency?.key)
          .join(', ')} is a catch-up step, so it will be skipped for some runs. Depending on it ` +
          'would block this step in those runs.',
      );
      return;
    }

    const badScopes = Object.keys(draft.requires || {}).filter(
      (scope) => !SCOPE_VALUES.includes(scope),
    );

    if (badScopes.length) {
      setFormError(
        `Preconditions has unknown scope${badScopes.length === 1 ? '' : 's'}: ${badScopes.join(
          ', ',
        )}. The loader accepts only ${SCOPE_VALUES.join(', ')}.`,
      );
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync(draft);
      onSaved?.(saved as LabScenarioStep);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title={step ? `Edit step ${step.key}` : `New step in ${scenario.name}`}
      size="x-large"
      scrollable
      submitLabel={step ? 'Save step' : 'Create step'}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? <p className="m-0 text-sm text-amber-800">{submitError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-5">
        <TextField
          label="Name"
          value={draft.name || ''}
          onChange={(evt) => setName(evt.target.value)}
          helperText="What this step does, in the run view. The key below comes from it."
          fullWidth
        />

        <LabDerivedKey
          value={draft.key || ''}
          frozen={!!step}
          usage="Criteria, triggers and injections refer to the step by it."
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => set({ description: evt.target.value })}
          multiline
          minRows={2}
          fullWidth
        />

        <LabFieldRow columns={3}>
          <LabOptionSelect
            label="Phase"
            value={draft.phase}
            options={LAB_PHASES}
            onChange={(phase) => set({ phase })}
          />
          <LabOptionSelect
            label="Action"
            value={draft.action_type}
            options={LAB_ACTION_TYPES}
            onChange={(action_type) => set({ action_type })}
          />
          <TextField
            label="Order"
            type="number"
            value={draft.order ?? 0}
            onChange={(evt) => set({ order: Number(evt.target.value) })}
            fullWidth
          />
        </LabFieldRow>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-gray-700">Device</span>
          <select
            className="rounded border border-gray-300 p-2 text-sm"
            value={draft.device ?? ''}
            onChange={(evt) =>
              set({ device: evt.target.value === '' ? null : Number(evt.target.value) })
            }
          >
            <option value="">No device (runs on the utility VM)</option>
            {devices.map((device) => (
              <option value={device.id} key={device.id}>
                {`${device.device_key} - ${device.display_name || device.platform}`}
              </option>
            ))}
          </select>
        </label>

        <LabFieldRow columns={3}>
          <TextField
            label="Timeout (seconds)"
            type="number"
            value={draft.timeout_seconds ?? 120}
            onChange={(evt) => set({ timeout_seconds: Number(evt.target.value) })}
            fullWidth
          />
          <TextField
            label="Retry limit"
            type="number"
            value={draft.retry_limit ?? 0}
            onChange={(evt) => set({ retry_limit: Number(evt.target.value) })}
            fullWidth
          />
          <TextField
            label="Repeat every (seconds)"
            type="number"
            value={draft.recurring_seconds ?? ''}
            onChange={(evt) =>
              set({
                recurring_seconds: evt.target.value === '' ? null : Number(evt.target.value),
              })
            }
            helperText="Leave empty to run once. Validation checks usually repeat."
            fullWidth
          />
        </LabFieldRow>

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.stepPayload}
          value={draft.payload}
          onChange={setJson('payload')}
          resetKey={formSeed}
          context={specContext}
        />

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.stepExpect}
          value={draft.expect}
          onChange={setJson('expect')}
          resetKey={formSeed}
          context={specContext}
        >
          <p className="m-0 text-xs text-gray-500">
            Without an expectation a step only records that it ran, and the run passes on having
            executed rather than on having worked.
          </p>
        </LabSpecBuilder>

        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.stepEmitsFacts}
          value={draft.emits_facts}
          onChange={setJson('emits_facts')}
          resetKey={formSeed}
          context={specContext}
        />

        {/* The scope editor, not a textarea: the loader refuses any key that is not one of the nine,
            and a scope typed by hand is carried, ignored, and reported one compile later. */}
        <LabSpecBuilder
          schema={LAB_JSON_SCHEMAS.stepRequires}
          value={draft.requires}
          onChange={setJson('requires')}
          resetKey={formSeed}
          context={specContext}
        />

        {/* --- Sequencing --- */}
        <section className="rounded-md border border-gray-200 p-3">
          <h4 className="m-0 mb-2 text-sm font-semibold">Sequencing</h4>

          <div className="flex flex-col gap-3">
            <LabCheckbox
              label="Gated"
              checked={!!draft.gated}
              onChange={(gated) => set({ gated })}
              helpText="Stays blocked until a trigger releases it. Nothing else opens a gated step."
            />

            <LabCheckbox
              label="Optional"
              checked={!!draft.optional}
              onChange={(optional) => set({ optional })}
              helpText="A failure here does not fail the run."
            />

            <div>
              <div className="text-sm font-medium text-gray-700">Depends on</div>
              <div className="mt-1 max-h-40 overflow-y-auto rounded border border-gray-200 p-2">
                {siblings.length ? (
                  siblings.map((sibling) => (
                    <label className="flex items-center gap-2 text-sm" key={sibling.id}>
                      <input
                        type="checkbox"
                        checked={(draft.depends_on || []).includes(sibling.id)}
                        onChange={() => toggleDependency(sibling.id)}
                      />
                      <span>
                        {`${sibling.key} (${sibling.phase})`}
                        {sibling.insert_mode === 'catch_up' && (
                          <span className="ml-1 text-xs text-amber-800">
                            catch-up - not safe to depend on
                          </span>
                        )}
                      </span>
                    </label>
                  ))
                ) : (
                  <span className="text-sm text-gray-500">No other steps yet.</span>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                This step stays blocked until every step listed here has succeeded.
              </div>
            </div>
          </div>
        </section>

        {/* --- Mid-session insertion --- */}
        <section className="rounded-md border border-gray-200 p-3">
          <h4 className="m-0 mb-2 text-sm font-semibold">Mid-session insertion</h4>

          <div className="flex flex-col gap-3">
            <LabOptionSelect
              label="Insert mode"
              value={draft.insert_mode}
              options={LAB_INSERT_MODES}
              onChange={(insert_mode) =>
                set({ insert_mode, ...(insert_mode === 'catch_up' ? {} : { skip_if_past: null }) })
              }
            />

            {draft.insert_mode === 'catch_up' && (
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-gray-700">Anchor step</span>
                <select
                  className="rounded border border-gray-300 p-2 text-sm"
                  value={draft.skip_if_past ?? ''}
                  onChange={(evt) =>
                    set({
                      skip_if_past: evt.target.value === '' ? null : Number(evt.target.value),
                    })
                  }
                >
                  <option value="">Choose the step this catches up to</option>
                  {siblings.map((sibling) => (
                    <option value={sibling.id} key={sibling.id}>
                      {`${sibling.key} (${sibling.phase})`}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">
                  Anyone who already finished this step is skipped, so their progress is not
                  disturbed.
                </span>
              </label>
            )}

            {injectionNotes.length > 0 && (
              <div className="rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-600">
                {injectionNotes.map((note) => (
                  <div key={note}>{note}</div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* --- Reach --- */}
        <section className="rounded-md border border-gray-200 p-3">
          <h4 className="m-0 mb-2 text-sm font-semibold">Reach</h4>
          <p className="m-0 mb-3 text-xs text-gray-500">
            Neither of these has a default. Unset means nobody has judged this step, and an
            unjudged step is never pushed at a live pod.
          </p>

          <div className="flex flex-col gap-3">
            <LabTristate
              label="Disruptive"
              value={draft.disruptive}
              onChange={(disruptive) => set({ disruptive })}
              helpText="Whether running this interrupts what the learner is doing."
            />

            <LabOptionSelect
              label="Blast radius"
              value={draft.blast_radius}
              options={LAB_BLAST_RADII}
              onChange={(blast_radius) => set({ blast_radius: blast_radius || null })}
              allowEmpty
              emptyLabel="Not stated"
            />

            <LabCheckbox
              label="Destructive"
              checked={!!draft.destructive}
              onChange={(destructive) => set({ destructive })}
              helpText="Removes or overwrites something the learner cannot get back."
            />

            <LabCheckbox
              label="Idempotent"
              checked={!!draft.idempotent}
              onChange={(idempotent) => set({ idempotent })}
              helpText="Safe to run twice. A non-idempotent step is not retried automatically."
            />

            <LabCheckbox
              label="Exclusive"
              checked={!!draft.exclusive}
              onChange={(exclusive) => set({ exclusive })}
              helpText="Nothing else in the pod runs while this does."
            />

            <LabCheckbox
              label="Sensitive output"
              checked={!!draft.sensitive_output}
              onChange={(sensitive_output) => set({ sensitive_output })}
              helpText="Output is withheld from the run view. Use it where a command echoes a secret."
            />
          </div>
        </section>

        <LabFieldRow>
          <LabOptionSelect
            label="Authority"
            value={draft.authority}
            options={LAB_AUTHORITIES}
            onChange={(authority) => set({ authority })}
            helpText="Who this step runs on behalf of."
          />
          <div className="flex items-end">
            <LabCheckbox
              label="Enabled"
              checked={!!draft.enabled}
              onChange={(enabled) => set({ enabled })}
              helpText="A disabled step is not materialized into new runs and cannot be injected."
            />
          </div>
        </LabFieldRow>
      </div>
    </StbModal>
  );
};

export default LabStepEditorModal;
